import React, { useState, useCallback } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import Modal, { MODAL_CONTENT_THEME } from "components/v3/modal/Modal";
import Button, { BUTTON_THEME_LINK } from "components/v3/button/Button";
import LoadingModal from "components/v3/loading/LoadingModal";
import saveOffers, { SAVE_OFFER_DO_NOT_HANDLE_ERROR, setSaveOfferIsProcessing } from "ducks/saveOffers";
import { ABBA_CONFIGS } from "common/constants/Constants";
import OfferScreen from "./OfferScreen";
import ConfirmationScreen from "./ConfirmationScreen";
import SuccessScreen from "./SuccessScreen";
import GoogleTracking from "services/GoogleTracking";
import entitlements from "ducks/entitlements";
import styles from "./styles.module.scss";

const STEPS = [ OfferScreen, ConfirmationScreen, SuccessScreen ];

const mapStateToProps = ({
    saveOffers: {
        allOffers,
        selectedOffer,
        loading,
        processing
    },
    config: {
        bloombergSiteUrl
    },
    abba: {
        config: {
            [ABBA_CONFIGS.SAVE_OFFERS]: {
                showSaveOffers
            } = {
                showSaveOffers: false
            }
        }
    },
    entitlements: {
        errors
    }
}, {
    nextRenewalDate,
    productType,
    subscriptionId,
    renewalPrice,
    billingPeriod
}) => ({
    allOffers,
    selectedOffer,
    bloombergSiteUrl,
    nextRenewalDate,
    productType,
    showSaveOffers,
    subscriptionId,
    renewalPrice,
    billingPeriod,
    loading,
    processing,
    errors
});

const mapDispatchToProps = (dispatch) => ({
    fetchRatePlans(productType, promotionType, renewalPrice, billingPeriod) {
        dispatch(saveOffers.setSaveOfferIsLoading(true));
        dispatch(saveOffers.fetchRatePlanInformation(productType, promotionType, renewalPrice, billingPeriod))
            .then(() => {
                dispatch(saveOffers.setSaveOfferIsLoading(false));
            })
            .catch((error) => {
                if (error.message === SAVE_OFFER_DO_NOT_HANDLE_ERROR) {
                    return;
                }

                dispatch(saveOffers.setSaveOfferIsLoading(false));
            });
    },
    setSelectedOffer(index) {
        return dispatch(saveOffers.setSelectedOffer(index));
    },
    updateSubscription(subscriptionId, ratePlanId) {
        dispatch(setSaveOfferIsProcessing(true));
        GoogleTracking.registerSaveOfferEvent("subscriber_save_confirm");

        return dispatch(entitlements.updateSubscription(subscriptionId, ratePlanId))
            .catch((error) => {
                dispatch(setSaveOfferIsProcessing(false));
                
                throw error;
            });
    }
});

export const SaveOfferModal = (props) => {
    const {
        allOffers,
        selectedOffer,
        bloombergSiteUrl,
        nextRenewalDate,
        fetchRatePlans,
        setSelectedOffer,
        productType,
        promotionType,
        subscriptionId,
        renewalPrice,
        billingPeriod,
        showSaveOffers,
        loading,
        processing,
        updateSubscription,
        errors
    } = props;
    const [isModalOpen, setShowModal] = useState(false);
    const [currentStep, setStep] = useState(0);
    const hideModal = useCallback(() => {
        setShowModal(false);
        setStep(0);
    }, []);
    const showModal = useCallback(() => {
        GoogleTracking.registerSaveOfferEvent("cancel_subscription");
        setShowModal(true);
        fetchRatePlans(productType, promotionType, renewalPrice, billingPeriod);
    }, [fetchRatePlans, productType, promotionType, renewalPrice, billingPeriod]);
    const next = useCallback(() => setStep(currentStep + 1), [currentStep]);

    const Step = STEPS[currentStep];
    const modalTheme = currentStep === 0 ? MODAL_CONTENT_THEME.CONCRETE : MODAL_CONTENT_THEME.DEFAULT;

    return (
        <>
            <Button
                text="Contact support to cancel"
                onClick={ showModal }
                theme={ BUTTON_THEME_LINK }
            />
            {
                showSaveOffers && loading && <LoadingModal />
            }
            {
                showSaveOffers && !loading && (
                    <Modal
                        isOpen={ isModalOpen }
                        onClose={ hideModal }
                        isInformational={ false }
                        modalContentTheme={ modalTheme }
                    >
                        <div className={ styles.stepContainer }>
                            <Step
                                next={ next }
                                allOffers={ allOffers }
                                selectedOffer={ selectedOffer }
                                bloombergSiteUrl={ bloombergSiteUrl }
                                nextRenewalDate={ nextRenewalDate }
                                setSelectedOffer={ setSelectedOffer }
                                subscriptionId={ subscriptionId }
                                thinking={ processing }
                                updateSubscription={ updateSubscription }
                                errors={ errors }
                            />
                        </div>
                    </Modal>
                )
            }
        </>
    );
};

SaveOfferModal.propTypes = {
    allOffers: PropTypes.array.isRequired,
    selectedOffer: PropTypes.number.isRequired,
    bloombergSiteUrl: PropTypes.string.isRequired,
    nextRenewalDate: PropTypes.string.isRequired,
    fetchRatePlans: PropTypes.func.isRequired,
    setSelectedOffer: PropTypes.func.isRequired,
    productType: PropTypes.string.isRequired,
    subscriptionId: PropTypes.number.isRequired,
    promotionType: PropTypes.string,
    errors: PropTypes.object
};

export default connect(mapStateToProps, mapDispatchToProps)(SaveOfferModal);
